import ArrowsDownToPeopleIcon from '@eversports/design-tokens/assets/icons/regular/arrows-down-to-people.svg'
import Box from '@eversports/klimt-primitives/legacy/Box'
import Button from '@eversports/klimt-primitives/legacy/Button'
import Heading from '@eversports/klimt-primitives/legacy/Heading'
import Icon from '@eversports/klimt-primitives/legacy/Icon'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Text from '@eversports/klimt-primitives/legacy/Text'
import Tooltip from '@eversports/klimt-primitives/legacy/Tooltip'
import { useState } from 'react'

import CopyIcon from '../../../../assets/icons/copy.svg'
import ShareIcon from '../../../../assets/icons/share.svg'
import { Localized, useIntl } from '../../../../localization/react'

interface Props {
  venueName: string
  referralCode: string
  rewardDescription: string
  image: string
}

export function VenueReferAFriendOverlayContent({ venueName, referralCode, rewardDescription, image }: Props) {
  const intl = useIntl()
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false)

  const handleCopy = () => {
    void navigator.clipboard.writeText(referralCode).then(() => {
      setShowCopiedTooltip(true)
      setTimeout(() => setShowCopiedTooltip(false), 1500)
    })
  }

  const handleShare = () => {
    if (typeof navigator === 'undefined' || !navigator.share) return
    void navigator
      .share({
        title: intl.referAFriendVenueDialogShareTitle({ venueName }),
        text: intl.referAFriendVenueDialogShareText({ venueName, referralCode }),
      })
      .catch(() => {
        // user cancelled or share unavailable
      })
  }

  return (
    <Stack gap={4}>
      <Stack direction="row" alignItems="center" gap={2}>
        <Icon src={ArrowsDownToPeopleIcon} size="small" color="midnight" />
        <Heading is="h4" variant="small">
          <Localized id="refer-a-friend-venue-dialog-title" params={{ venueName }} />
        </Heading>
      </Stack>

      <Box
        component="img"
        src={image}
        alt=""
        sx={{
          width: '100%',
          objectFit: 'cover',
          borderRadius: 1,
        }}
      />

      {rewardDescription && (
        <Text variant="small" sx={{ color: 'darkgray' }}>
          {rewardDescription}
        </Text>
      )}

      <Stack gap={1}>
        <Text variant="small" sx={{ fontWeight: 'bold' }}>
          <Localized id="refer-a-friend-venue-dialog-code-label-at-venue" params={{ venueName }} />
        </Text>
        <Tooltip title={<Localized id="tooltip-copied-text" />} arrow placement="top" open={showCopiedTooltip}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap={2}
            onClick={handleCopy}
            role="button"
            aria-label={referralCode}
            sx={{
              border: '1px solid',
              borderColor: 'mediumgray',
              borderRadius: 1,
              padding: 3,
              backgroundColor: 'white',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              '&:hover': { backgroundColor: 'lightgray' },
            }}
          >
            <Text variant="small" sx={{ fontWeight: 'medium', color: 'darkgray' }}>
              {referralCode}
            </Text>
            <Icon src={CopyIcon} size="small" color="darkgray" />
          </Stack>
        </Tooltip>
      </Stack>

      <Button
        variant="primary"
        size="large"
        fullWidth
        onClick={handleShare}
        sx={{ textTransform: 'none' }}
        startIcon={<Icon src={ShareIcon} size="small" color="white" />}
      >
        <Text variant="small" sx={{ fontWeight: 'medium', color: 'white' }}>
          <Localized id="refer-a-friend-venue-dialog-share-button" />
        </Text>
      </Button>
    </Stack>
  )
}
